import { VISION_RADIUS } from '../utils/constants';

/**
 * Procedural audio manager using Web Audio API.
 * Generates creepy ambient drone and proximity-based wraith sounds.
 */
export class SoundManager {
  private ctx: AudioContext | null = null;
  private masterGain!: GainNode;

  // Ambient drone nodes
  private droneOsc1!: OscillatorNode;
  private droneOsc2!: OscillatorNode;
  private droneLfo!: OscillatorNode;
  private droneLfoGain!: GainNode;
  private droneGain!: GainNode;
  private droneFilter!: BiquadFilterNode;

  // Wraith sound nodes
  private wraithNoise!: AudioBufferSourceNode;
  private wraithFilter!: BiquadFilterNode;
  private wraithGain!: GainNode;
  private wraithLfo!: OscillatorNode;
  private wraithLfoGain!: GainNode;

  private started = false;

  /** Call on first user interaction (click/keydown) to unlock audio */
  start(): void {
    if (this.started) return;
    this.started = true;

    this.ctx = new AudioContext();
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = 0.6;
    this.masterGain.connect(this.ctx.destination);

    this.setupDrone();
    this.setupWraith();
  }

  private setupDrone(): void {
    const ctx = this.ctx!;

    // Low drone oscillator 1
    this.droneOsc1 = ctx.createOscillator();
    this.droneOsc1.type = 'sawtooth';
    this.droneOsc1.frequency.value = 55; // A1

    // Low drone oscillator 2 (slightly detuned for beating)
    this.droneOsc2 = ctx.createOscillator();
    this.droneOsc2.type = 'sawtooth';
    this.droneOsc2.frequency.value = 55.5;

    // LFO for slow volume pulsing
    this.droneLfo = ctx.createOscillator();
    this.droneLfo.type = 'sine';
    this.droneLfo.frequency.value = 0.15; // Very slow pulse

    this.droneLfoGain = ctx.createGain();
    this.droneLfoGain.gain.value = 0.03;

    // Drone gain
    this.droneGain = ctx.createGain();
    this.droneGain.gain.value = 0.07;

    // Dark low-pass filter
    this.droneFilter = ctx.createBiquadFilter();
    this.droneFilter.type = 'lowpass';
    this.droneFilter.frequency.value = 200;
    this.droneFilter.Q.value = 2;

    // Connect: oscs → filter → gain → master
    this.droneOsc1.connect(this.droneFilter);
    this.droneOsc2.connect(this.droneFilter);
    this.droneFilter.connect(this.droneGain);

    // LFO modulates drone gain
    this.droneLfo.connect(this.droneLfoGain);
    this.droneLfoGain.connect(this.droneGain.gain);

    this.droneGain.connect(this.masterGain);

    this.droneOsc1.start();
    this.droneOsc2.start();
    this.droneLfo.start();

    // Add a second layer: sub-bass sine for weight
    const subBass = ctx.createOscillator();
    subBass.type = 'sine';
    subBass.frequency.value = 36; // C1-ish
    const subGain = ctx.createGain();
    subGain.gain.value = 0.05;
    subBass.connect(subGain);
    subGain.connect(this.masterGain);
    subBass.start();

    // Occasional dissonant high tone
    const dissonant = ctx.createOscillator();
    dissonant.type = 'sine';
    dissonant.frequency.value = 311; // Eb4 — minor interval against A
    const disGain = ctx.createGain();
    disGain.gain.value = 0.008;
    const disLfo = ctx.createOscillator();
    disLfo.type = 'sine';
    disLfo.frequency.value = 0.08;
    const disLfoGain = ctx.createGain();
    disLfoGain.gain.value = 0.008;
    dissonant.connect(disGain);
    disLfo.connect(disLfoGain);
    disLfoGain.connect(disGain.gain);
    disGain.connect(this.masterGain);
    dissonant.start();
    disLfo.start();
  }

  private setupWraith(): void {
    const ctx = this.ctx!;

    // Create noise buffer for wraith hiss
    const bufferSize = ctx.sampleRate * 2;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1);
    }

    this.wraithNoise = ctx.createBufferSource();
    this.wraithNoise.buffer = buffer;
    this.wraithNoise.loop = true;

    // Band-pass filter for breathy/spectral quality
    this.wraithFilter = ctx.createBiquadFilter();
    this.wraithFilter.type = 'bandpass';
    this.wraithFilter.frequency.value = 800;
    this.wraithFilter.Q.value = 5;

    // LFO to sweep the filter frequency (ghostly waver)
    this.wraithLfo = ctx.createOscillator();
    this.wraithLfo.type = 'sine';
    this.wraithLfo.frequency.value = 0.4;

    this.wraithLfoGain = ctx.createGain();
    this.wraithLfoGain.gain.value = 400;

    // Volume (controlled by proximity)
    this.wraithGain = ctx.createGain();
    this.wraithGain.gain.value = 0;

    // Connect: noise → filter → gain → master
    this.wraithNoise.connect(this.wraithFilter);
    this.wraithFilter.connect(this.wraithGain);
    this.wraithLfo.connect(this.wraithLfoGain);
    this.wraithLfoGain.connect(this.wraithFilter.frequency);
    this.wraithGain.connect(this.masterGain);

    this.wraithNoise.start();
    this.wraithLfo.start();

    // Add a tonal wraith whisper — detuned sine pair
    const wraithTone1 = ctx.createOscillator();
    wraithTone1.type = 'sine';
    wraithTone1.frequency.value = 440;
    const wraithTone2 = ctx.createOscillator();
    wraithTone2.type = 'sine';
    wraithTone2.frequency.value = 447; // Slight beating

    const toneFilter = ctx.createBiquadFilter();
    toneFilter.type = 'bandpass';
    toneFilter.frequency.value = 600;
    toneFilter.Q.value = 3;

    // These share the wraith gain (proximity-controlled)
    const toneGain = ctx.createGain();
    toneGain.gain.value = 0.15;

    wraithTone1.connect(toneFilter);
    wraithTone2.connect(toneFilter);
    toneFilter.connect(toneGain);
    toneGain.connect(this.wraithGain);

    wraithTone1.start();
    wraithTone2.start();
  }

  /**
   * Update wraith sound intensity based on closest Remnant distance.
   * @param closestDist Manhattan distance to nearest Remnant
   */
  updateWraithProximity(closestDist: number): void {
    if (!this.ctx || !this.wraithGain) return;

    const maxDist = VISION_RADIUS + 2; // Start fading in just outside vision
    const t = this.ctx.currentTime;

    if (closestDist >= maxDist) {
      this.wraithGain.gain.linearRampToValueAtTime(0, t + 0.1);
      this.wraithFilter.frequency.linearRampToValueAtTime(600, t + 0.1);
    } else {
      // Intensity: 0 at maxDist, 1 at dist=0
      const intensity = 1 - closestDist / maxDist;
      const volume = intensity * intensity * 0.35; // Quadratic for dramatic close-range spike
      const filterFreq = 600 + intensity * 1200; // Higher pitch when closer

      this.wraithGain.gain.linearRampToValueAtTime(volume, t + 0.15);
      this.wraithFilter.frequency.linearRampToValueAtTime(filterFreq, t + 0.15);
      this.wraithLfo.frequency.linearRampToValueAtTime(0.4 + intensity * 2, t + 0.15);
    }
  }

  /** Play a short footstep sound — filtered noise click */
  playFootstep(): void {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;

    // Short burst of filtered noise
    const bufLen = Math.floor(ctx.sampleRate * 0.06);
    const buf = ctx.createBuffer(1, bufLen, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < bufLen; i++) {
      data[i] = (Math.random() * 2 - 1);
    }

    const src = ctx.createBufferSource();
    src.buffer = buf;

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 600 + Math.random() * 200; // Slight variation
    filter.Q.value = 1;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.12, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.08);

    src.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);
    src.start(t);
    src.stop(t + 0.08);
  }

  /** Play a dramatic slicing death sound — multi-layered */
  playScream(): void {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;

    // === Layer 1: Initial impact thud (the hit) ===
    const impactOsc = ctx.createOscillator();
    impactOsc.type = 'sine';
    impactOsc.frequency.setValueAtTime(80, t);
    impactOsc.frequency.exponentialRampToValueAtTime(20, t + 0.3);
    const impactGain = ctx.createGain();
    impactGain.gain.setValueAtTime(0.5, t);
    impactGain.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
    impactOsc.connect(impactGain);
    impactGain.connect(this.masterGain);
    impactOsc.start(t);
    impactOsc.stop(t + 0.3);

    // === Layer 2: Slicing sounds — 3 rapid high-pitched sweeps ===
    for (let i = 0; i < 3; i++) {
      const sliceStart = t + 0.15 + i * 0.2;
      const sliceDur = 0.15;

      const sliceBuf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * sliceDur), ctx.sampleRate);
      const sliceData = sliceBuf.getChannelData(0);
      for (let j = 0; j < sliceData.length; j++) sliceData[j] = Math.random() * 2 - 1;

      const sliceSrc = ctx.createBufferSource();
      sliceSrc.buffer = sliceBuf;

      const sliceFilter = ctx.createBiquadFilter();
      sliceFilter.type = 'highpass';
      sliceFilter.frequency.setValueAtTime(3000 + i * 500, sliceStart);
      sliceFilter.frequency.exponentialRampToValueAtTime(800, sliceStart + sliceDur);
      sliceFilter.Q.value = 10;

      const sliceGain = ctx.createGain();
      sliceGain.gain.setValueAtTime(0.3, sliceStart);
      sliceGain.gain.exponentialRampToValueAtTime(0.001, sliceStart + sliceDur);

      sliceSrc.connect(sliceFilter);
      sliceFilter.connect(sliceGain);
      sliceGain.connect(this.masterGain);
      sliceSrc.start(sliceStart);
      sliceSrc.stop(sliceStart + sliceDur);
    }

    // === Layer 3: Scream / wail — descending dissonant tones ===
    const screamDur = 2.0;
    const screamStart = t + 0.1;

    const screamOsc1 = ctx.createOscillator();
    screamOsc1.type = 'sawtooth';
    screamOsc1.frequency.setValueAtTime(1100, screamStart);
    screamOsc1.frequency.exponentialRampToValueAtTime(150, screamStart + screamDur);

    const screamOsc2 = ctx.createOscillator();
    screamOsc2.type = 'sawtooth';
    screamOsc2.frequency.setValueAtTime(1113, screamStart);
    screamOsc2.frequency.exponentialRampToValueAtTime(153, screamStart + screamDur);

    const screamOsc3 = ctx.createOscillator();
    screamOsc3.type = 'square';
    screamOsc3.frequency.setValueAtTime(550, screamStart);
    screamOsc3.frequency.exponentialRampToValueAtTime(80, screamStart + screamDur);

    const screamFilter = ctx.createBiquadFilter();
    screamFilter.type = 'bandpass';
    screamFilter.frequency.setValueAtTime(1500, screamStart);
    screamFilter.frequency.exponentialRampToValueAtTime(200, screamStart + screamDur);
    screamFilter.Q.value = 3;

    const screamGain = ctx.createGain();
    screamGain.gain.setValueAtTime(0.25, screamStart);
    screamGain.gain.linearRampToValueAtTime(0.18, screamStart + 0.5);
    screamGain.gain.exponentialRampToValueAtTime(0.001, screamStart + screamDur);

    screamOsc1.connect(screamFilter);
    screamOsc2.connect(screamFilter);
    screamOsc3.connect(screamFilter);
    screamFilter.connect(screamGain);
    screamGain.connect(this.masterGain);
    screamOsc1.start(screamStart);
    screamOsc2.start(screamStart);
    screamOsc3.start(screamStart);
    screamOsc1.stop(screamStart + screamDur);
    screamOsc2.stop(screamStart + screamDur);
    screamOsc3.stop(screamStart + screamDur);

    // === Layer 4: Wet tearing noise — filtered noise sustain ===
    const tearDur = 1.5;
    const tearStart = t + 0.2;
    const tearBufLen = Math.floor(ctx.sampleRate * tearDur);
    const tearBuf = ctx.createBuffer(1, tearBufLen, ctx.sampleRate);
    const tearData = tearBuf.getChannelData(0);
    for (let i = 0; i < tearBufLen; i++) tearData[i] = Math.random() * 2 - 1;

    const tearSrc = ctx.createBufferSource();
    tearSrc.buffer = tearBuf;

    const tearFilter = ctx.createBiquadFilter();
    tearFilter.type = 'bandpass';
    tearFilter.frequency.setValueAtTime(1200, tearStart);
    tearFilter.frequency.exponentialRampToValueAtTime(300, tearStart + tearDur);
    tearFilter.Q.value = 6;

    const tearGain = ctx.createGain();
    tearGain.gain.setValueAtTime(0.0, tearStart);
    tearGain.gain.linearRampToValueAtTime(0.3, tearStart + 0.15);
    tearGain.gain.linearRampToValueAtTime(0.15, tearStart + 0.6);
    tearGain.gain.exponentialRampToValueAtTime(0.001, tearStart + tearDur);

    tearSrc.connect(tearFilter);
    tearFilter.connect(tearGain);
    tearGain.connect(this.masterGain);
    tearSrc.start(tearStart);
    tearSrc.stop(tearStart + tearDur);

    // === Layer 5: Low rumble aftermath ===
    const rumbleOsc = ctx.createOscillator();
    rumbleOsc.type = 'sine';
    rumbleOsc.frequency.value = 40;
    const rumbleGain = ctx.createGain();
    rumbleGain.gain.setValueAtTime(0, t + 0.8);
    rumbleGain.gain.linearRampToValueAtTime(0.15, t + 1.0);
    rumbleGain.gain.exponentialRampToValueAtTime(0.001, t + 2.5);
    rumbleOsc.connect(rumbleGain);
    rumbleGain.connect(this.masterGain);
    rumbleOsc.start(t + 0.8);
    rumbleOsc.stop(t + 2.5);
  }

  /** Stone sliding into place — low thud with resonance */
  playWallBuild(): void {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;

    // Impact thud
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(120, t);
    osc.frequency.exponentialRampToValueAtTime(50, t + 0.2);

    // Noise layer for grit
    const bufLen = Math.floor(ctx.sampleRate * 0.15);
    const buf = ctx.createBuffer(1, bufLen, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < bufLen; i++) data[i] = Math.random() * 2 - 1;
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const nf = ctx.createBiquadFilter();
    nf.type = 'lowpass';
    nf.frequency.value = 400;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.2, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.25);

    osc.connect(gain);
    noise.connect(nf);
    nf.connect(gain);
    gain.connect(this.masterGain);
    osc.start(t);
    osc.stop(t + 0.25);
    noise.start(t);
    noise.stop(t + 0.15);
  }

  /** Stone crumbling away — noise burst with descending filter */
  playWallDemolish(): void {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;

    const bufLen = Math.floor(ctx.sampleRate * 0.3);
    const buf = ctx.createBuffer(1, bufLen, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < bufLen; i++) data[i] = Math.random() * 2 - 1;
    const noise = ctx.createBufferSource();
    noise.buffer = buf;

    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(800, t);
    filter.frequency.exponentialRampToValueAtTime(150, t + 0.3);
    filter.Q.value = 2;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.18, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.35);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);
    noise.start(t);
    noise.stop(t + 0.35);
  }

  /** Ascending shimmer + whoosh teleport sound */
  playTeleport(): void {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const duration = 1.5;

    // Layer 1: Ascending shimmer — rising sine with vibrato
    const shimmer = ctx.createOscillator();
    shimmer.type = 'sine';
    shimmer.frequency.setValueAtTime(300, t);
    shimmer.frequency.exponentialRampToValueAtTime(2000, t + duration);

    const shimmer2 = ctx.createOscillator();
    shimmer2.type = 'sine';
    shimmer2.frequency.setValueAtTime(305, t);
    shimmer2.frequency.exponentialRampToValueAtTime(2010, t + duration);

    const shimmerGain = ctx.createGain();
    shimmerGain.gain.setValueAtTime(0.15, t);
    shimmerGain.gain.linearRampToValueAtTime(0.25, t + 0.5);
    shimmerGain.gain.exponentialRampToValueAtTime(0.001, t + duration);

    shimmer.connect(shimmerGain);
    shimmer2.connect(shimmerGain);
    shimmerGain.connect(this.masterGain);
    shimmer.start(t);
    shimmer2.start(t);
    shimmer.stop(t + duration);
    shimmer2.stop(t + duration);

    // Layer 2: Sparkle — high-frequency filtered noise burst
    const sparkleBufLen = Math.floor(ctx.sampleRate * 0.8);
    const sparkleBuf = ctx.createBuffer(1, sparkleBufLen, ctx.sampleRate);
    const sparkleData = sparkleBuf.getChannelData(0);
    for (let i = 0; i < sparkleBufLen; i++) sparkleData[i] = Math.random() * 2 - 1;

    const sparkleSrc = ctx.createBufferSource();
    sparkleSrc.buffer = sparkleBuf;

    const sparkleFilter = ctx.createBiquadFilter();
    sparkleFilter.type = 'highpass';
    sparkleFilter.frequency.setValueAtTime(4000, t);
    sparkleFilter.frequency.exponentialRampToValueAtTime(8000, t + 0.8);
    sparkleFilter.Q.value = 3;

    const sparkleGain = ctx.createGain();
    sparkleGain.gain.setValueAtTime(0.1, t);
    sparkleGain.gain.linearRampToValueAtTime(0.2, t + 0.3);
    sparkleGain.gain.exponentialRampToValueAtTime(0.001, t + 0.8);

    sparkleSrc.connect(sparkleFilter);
    sparkleFilter.connect(sparkleGain);
    sparkleGain.connect(this.masterGain);
    sparkleSrc.start(t);
    sparkleSrc.stop(t + 0.8);

    // Layer 3: Whoosh — descending noise at the end
    const whooshBufLen = Math.floor(ctx.sampleRate * 0.4);
    const whooshBuf = ctx.createBuffer(1, whooshBufLen, ctx.sampleRate);
    const whooshData = whooshBuf.getChannelData(0);
    for (let i = 0; i < whooshBufLen; i++) whooshData[i] = Math.random() * 2 - 1;

    const whooshSrc = ctx.createBufferSource();
    whooshSrc.buffer = whooshBuf;

    const whooshFilter = ctx.createBiquadFilter();
    whooshFilter.type = 'bandpass';
    whooshFilter.frequency.setValueAtTime(2000, t + 0.8);
    whooshFilter.frequency.exponentialRampToValueAtTime(200, t + 1.3);
    whooshFilter.Q.value = 2;

    const whooshGain = ctx.createGain();
    whooshGain.gain.setValueAtTime(0.0, t + 0.8);
    whooshGain.gain.linearRampToValueAtTime(0.25, t + 0.9);
    whooshGain.gain.exponentialRampToValueAtTime(0.001, t + 1.3);

    whooshSrc.connect(whooshFilter);
    whooshFilter.connect(whooshGain);
    whooshGain.connect(this.masterGain);
    whooshSrc.start(t + 0.8);
    whooshSrc.stop(t + 1.3);

    // Layer 4: Triumphant chime
    const chime = ctx.createOscillator();
    chime.type = 'sine';
    chime.frequency.value = 880;
    const chimeGain = ctx.createGain();
    chimeGain.gain.setValueAtTime(0.0, t + 1.0);
    chimeGain.gain.linearRampToValueAtTime(0.15, t + 1.05);
    chimeGain.gain.exponentialRampToValueAtTime(0.001, t + 1.5);
    chime.connect(chimeGain);
    chimeGain.connect(this.masterGain);
    chime.start(t + 1.0);
    chime.stop(t + 1.5);
  }

  stop(): void {
    if (this.ctx) {
      this.ctx.close();
      this.ctx = null;
      this.started = false;
    }
  }
}
